import {createAsyncThunk} from '@reduxjs/toolkit';

import postFacilityBreakdown from 'api/events/post-facility-breakdown';

import type {EventFacilityBreakdownItem} from 'api/events/events.types';
import type {EventFacilityBreakdownState} from './event-facility-breakdown-slice';
import {ApiResponse} from 'api/api.types';

const CANCELED = 'ERR_CANCELED';

export interface GetEventFacilityBreakdownParams {
    eventIds: number[];
    facilityIds: number[];
    offset: number;
    searchText: string;
    handleAbortSignal?: (signal: AbortSignal) => void;
    abortSignal?: AbortSignal;
}

type FormattedGetEventFacilityBreakdownResponse = ApiResponse<
    EventFacilityBreakdownItem[]
>['data'];

interface GetEventFacilityBreakdownThunkAPI {
    state: {
        events: {
            eventFacilityBreakdown: EventFacilityBreakdownState;
        };
    };
    rejectValue: unknown;
}

/**
 * A Redux thunk to get event facility breakdown data.
 *
 * Action Type: events/event-facility-breakdown/getEventFacilityBreakdown
 *
 * API Service: {@link postFacilityBreakdown}
 */
export const getEventFacilityBreakdown = createAsyncThunk<
    FormattedGetEventFacilityBreakdownResponse,
    GetEventFacilityBreakdownParams,
    GetEventFacilityBreakdownThunkAPI
>(
    'events/event-facility-breakdown/getEventFacilityBreakdown',
    async (
        {
            eventIds,
            facilityIds,
            offset = 0,
            searchText,
            handleAbortSignal,
            abortSignal,
        },
        {rejectWithValue, signal}
    ) => {
        try {
            if (handleAbortSignal) {
                handleAbortSignal(signal);
            }

            const response = await postFacilityBreakdown(
                eventIds,
                facilityIds,
                offset,
                searchText,
                {
                    signal: abortSignal,
                }
            );

            return response;
        } catch (error) {
            if (error?.code === CANCELED) {
                return;
            }

            console.error('getEventFacilityBreakdown error:', {error});

            return rejectWithValue(error);
        }
    }
);
